import { FunctionComponent } from 'react';
import Script from 'next/script';
import { jsonSchema } from './dataSchema';
import { getIndexedPlans, hoursToISO8601Duration, getHoursDuration } from './utils';


export const StructuredData: FunctionComponent<{ schoolData: any, lpData: any, plans: any }> = ({ schoolData = {}, lpData = {}, plans = {}}) => {
  const isSchool = Object.keys(schoolData).length > 0 || false;

  const commonProvider = {
    "@type": "Organization",
    "name": "Platzi"
  }

  const buildImageObject = (url: string) => {
    return {
      "@type": "ImageObject",
      "url": url
    }
  }

  const buildPlansData = () => {
    const parsedPlans = getIndexedPlans(plans?.plans);
    return Object.keys(parsedPlans)?.map((key: string) => ({
      "@type": "Offer",
      "category": "subscription",
      "eligibleDuration": {
        "@type": "Duration",
        "duration": "P12M"
      },
      "availability": "https://schema.org/OnlineOnly",
      "name": parsedPlans[key]?.title,
      "price": parsedPlans[key]?.full_price,
      "priceCurrency": parsedPlans[key]?.currency_code,
      "url": `https://platzi.com${parsedPlans[key]?.buy_url}`,
      "seller": {
        "@type": "Organization",
        "name": "Platzi"
      }
    }));
  };

  const buildOffers = () => {
    return {
      "@type": "AggregateOffer",
      "category": "Subscription",
      "offers": buildPlansData()
    }
  }

  const buildCourseInstance = (course: any) => {
    return {
      "@type": "CourseInstance",
      "courseMode": ["Online"],
      "courseSchedule": {
        "@type": "Schedule",
        "duration": hoursToISO8601Duration(course?.content_duration + course?.practice_duration),
        "startTime": "00:00",
        "endTime": "23:59",
        "scheduleTimezone": "UTC",
        "repeatFrequency": "Daily",
        "repeatCount": getHoursDuration(course),
      },
    }
  }

  const buildLearningPathsData = () => {
    const step = lpData?.steps?.[0];
    const courses = step?.courses;
    const educationalLevel = step?.level;
    return courses?.map((course, index) => ({
    "@type": "ListItem",
    "position": index + 2,
    "item":
      {
        "@type": "Course",
        "name": course?.title,
        "url": `https://platzi.com${course?.url}`,
        "description": course?.description,
        "provider": commonProvider,
        "learningResourceType": "Course",
        "educationalLevel": educationalLevel?.name || "",
        "courseWorkload": hoursToISO8601Duration(course?.content_duration + course?.practice_duration),
        "hasCourseInstance": {
          ...buildCourseInstance(course),
          "image": buildImageObject(course?.actual_material?.thumbnail)
        },
        "offers": buildOffers(),
      }
  }))
  };

  const buildSchoolData = () => {
    const dataToMap = schoolData?.tabLp;

    const allCourses = dataToMap?.flatMap(section => 
      section.routes?.flatMap((route: any) => 
        route.courses?.map((course: any) => ({
          "@type": "ListItem",
          "position": course.id,
          "item": {
          "@type": "Course",
          "name": course.title,
          "description": course.description,
          "provider": commonProvider,
          "learningResourceType": "Course",
          "educationalLevel": course.level || "",
          "courseWorkload": hoursToISO8601Duration(course?.content_duration + course?.practice_duration),
          "hasCourseInstance": {
            ...buildCourseInstance(course),
            "image": buildImageObject(course.thumbnail)
          },
          "offers": buildOffers(),
        }
      }))
      )
    );

    return allCourses?.filter(Boolean) || [];
  };

  const getStructuredData = isSchool ? buildSchoolData() : buildLearningPathsData();

  const buildSchema = () => {
    return {
      "@context": "https://schema.org",
      "@graph": [
        {...jsonSchema},
        {
          "@type": "ItemList",
          "itemListElement": [
            ...(getStructuredData ?? [])
          ]
        }
      ]
    }
  };

  return (
    <Script
      id="structured-data"
      strategy="lazyOnload"
      type="application/ld+json"
      async
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(buildSchema())
      }}
    />
  );
};
